import { useState, useEffect, useRef } from "react"
import { getAuth } from "firebase/auth"
import "../Css/CreateFileMenu.css"

export default function CreateFileMenu({ isOpen, position, onClose, onFileCreated }) {
  const baseURL = process.env.REACT_APP_URLBASE
  const [isCreating, setIsCreating] = useState(false)
  const [creatingType, setCreatingType] = useState(null)
  const menuRef = useRef(null)
  const auth = getAuth()
  const user = auth.currentUser

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose()
      }
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscape)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  // 🚀 MÉTODO SIMPLE: Crear archivos usando plantillas básicas
  const createFileFromTemplate = async (fileType) => {
    if (!user) return

    setIsCreating(true)
    setCreatingType(fileType)

    try {
      const fileName = generateFileName(fileType)
      const templateBlob = await generateTemplateFile(fileType)

      // Crear FormData para subir el archivo
      const formData = new FormData()
      formData.append("file", templateBlob, fileName)

      const response = await fetch(`${baseURL}/update/upload`, {
        method: "POST",
        headers: {
          "user-id": user.uid,
        },
        body: formData,
        credentials: "include",
      })

      if (response.ok) {
        const result = await response.json()
        console.log("✅ Archivo creado:", result)

        // Notificar al componente padre
        if (onFileCreated) {
          onFileCreated({
            fileName: fileName,
            r2Key: result.r2Key || fileName,
            fileType: fileType,
            isNew: true,
          })
        }

        onClose()
      } else {
        throw new Error("Error al crear el archivo")
      }
    } catch (error) {
      console.error("❌ Error creando archivo:", error)
      alert(`Error al crear ${fileType.toUpperCase()}: ${error.message}`)
    } finally {
      setIsCreating(false)
      setCreatingType(null)
    }
  }

  // Generar nombre único para el archivo
  const generateFileName = (fileType) => {
    const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, "").replace("T", "_")
    const typeNames = {
      docx: "Documento",
      xlsx: "Hoja_de_Calculo",
      pptx: "Presentacion",
    }
    return `${typeNames[fileType]}_${timestamp}.${fileType}`
  }

  // 🎯 PLANTILLAS MÍNIMAS: Crear archivos básicos sin OnlyOffice
  const generateTemplateFile = async (fileType) => {
    switch (fileType) {
      case "docx":
        return await createWordTemplate()
      case "xlsx":
        return await createExcelTemplate()
      case "pptx":
        return await createPowerPointTemplate()
      default:
        throw new Error("Tipo de archivo no soportado")
    }
  }

  // Plantilla mínima de Word (usando estructura ZIP básica)
  const createWordTemplate = async () => {
    // Importar JSZip dinámicamente para reducir bundle
    const JSZip = (await import("jszip")).default

    const zip = new JSZip()

    // Estructura mínima de un documento Word
    zip.file(
      "[Content_Types].xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
    )

    zip.folder("_rels").file(
      ".rels",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    )

    zip.folder("word").file(
      "document.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    <w:p>
      <w:r>
        <w:t>Nuevo documento creado en Cloud Private</w:t>
      </w:r>
    </w:p>
    <w:p>
      <w:r>
        <w:t>¡Comienza a escribir aquí!</w:t>
      </w:r>
    </w:p>
  </w:body>
</w:document>`,
    )

    return await zip.generateAsync({ type: "blob" })
  }

  // Plantilla mínima de Excel
  const createExcelTemplate = async () => {
    const JSZip = (await import("jszip")).default
    const zip = new JSZip()

    zip.file(
      "[Content_Types].xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`,
    )

    zip.folder("_rels").file(
      ".rels",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    )

    const xlFolder = zip.folder("xl")
    xlFolder.file(
      "workbook.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheets>
    <sheet name="Hoja1" sheetId="1" r:id="rId1" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/>
  </sheets>
</workbook>`,
    )

    xlFolder.folder("worksheets").file(
      "sheet1.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>
    <row r="1">
      <c r="A1" t="inlineStr">
        <is><t>Cloud Private</t></is>
      </c>
      <c r="B1" t="inlineStr">
        <is><t>Nueva hoja de cálculo</t></is>
      </c>
    </row>
  </sheetData>
</worksheet>`,
    )

    return await zip.generateAsync({ type: "blob" })
  }

  // Plantilla mínima de PowerPoint
  const createPowerPointTemplate = async () => {
    const JSZip = (await import("jszip")).default
    const zip = new JSZip()

    zip.file(
      "[Content_Types].xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>
  <Override PartName="/ppt/slides/slide1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>
</Types>`,
    )

    zip.folder("_rels").file(
      ".rels",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="ppt/presentation.xml"/>
</Relationships>`,
    )

    const pptFolder = zip.folder("ppt")
    pptFolder.file(
      "presentation.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:presentation xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:sldMasterIdLst>
    <p:sldMasterId id="2147483648" r:id="rId1" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/>
  </p:sldMasterIdLst>
  <p:sldIdLst>
    <p:sldId id="256" r:id="rId2" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/>
  </p:sldIdLst>
  <p:sldSz cx="9144000" cy="6858000"/>
</p:presentation>`,
    )

    pptFolder.folder("slides").file(
      "slide1.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sld xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:cSld>
    <p:spTree>
      <p:nvGrpSpPr>
        <p:cNvPr id="1" name=""/>
        <p:cNvGrpSpPr/>
        <p:nvPr/>
      </p:nvGrpSpPr>
      <p:grpSpPr>
        <a:xfrm xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
          <a:off x="0" y="0"/>
          <a:ext cx="0" cy="0"/>
          <a:chOff x="0" y="0"/>
          <a:chExt cx="0" cy="0"/>
        </a:xfrm>
      </p:grpSpPr>
      <p:sp>
        <p:nvSpPr>
          <p:cNvPr id="2" name="Título 1"/>
          <p:cNvSpPr>
            <a:spLocks noGrp="1" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"/>
          </p:cNvSpPr>
          <p:nvPr>
            <p:ph type="ctrTitle"/>
          </p:nvPr>
        </p:nvSpPr>
        <p:spPr/>
        <p:txBody>
          <a:bodyPr xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"/>
          <a:lstStyle xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"/>
          <a:p xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
            <a:r>
              <a:rPr lang="es-ES" dirty="0" smtClean="0"/>
              <a:t>Nueva Presentación</a:t>
            </a:r>
          </a:p>
        </p:txBody>
      </p:sp>
    </p:spTree>
  </p:cSld>
</p:sld>`,
    )

    return await zip.generateAsync({ type: "blob" })
  }

  if (!isOpen) return null

  const menuItems = [
    {
      type: "docx",
      icon: "📄",
      label: "Documento Word",
      description: "Crear nuevo documento",
    },
    {
      type: "xlsx",
      icon: "📊",
      label: "Hoja de Cálculo",
      description: "Crear nueva hoja Excel",
    },
    {
      type: "pptx",
      icon: "📽️",
      label: "Presentación",
      description: "Crear nueva presentación",
    },
  ]

  return (
    <div
      className="create-file-menu"
      ref={menuRef}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="menu-header">
        <span className="menu-title">✨ Crear nuevo archivo</span>
      </div>

      <div className="menu-items">
        {menuItems.map((item) => (
          <button
            key={item.type}
            className={`menu-item ${isCreating && creatingType === item.type ? "creating" : ""}`}
            onClick={() => createFileFromTemplate(item.type)}
            disabled={isCreating}
          >
            <div className="item-icon">{item.icon}</div>
            <div className="item-content">
              <div className="item-label">{item.label}</div>
              <div className="item-description">{item.description}</div>
            </div>
            {isCreating && creatingType === item.type && (
              <div className="creating-spinner">
                <div className="spinner"></div>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="menu-footer">
        <small>Los archivos se crearán listos para editar</small>
      </div>
    </div>
  )
}
